import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { Category } from '../types';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';

const CategoryList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extraFields, setExtraFields] = useState<string[]>([]);
  const { showError, showSuccess } = useToast();
  
  // Helper to display category names consistently
  const formatCategoryName = (categoryName: string) => categoryName ?? '';

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Category>();

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle browser back button to close modal
  useEffect(() => {
    if (!isFormOpen) return;

    const handlePopState = () => {
      setIsFormOpen(false);
      setEditingCategory(undefined);
      setExtraFields([]);
      reset({
        name: ''
      });
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFormOpen, reset]);

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      if (response.status && response.data) {
        setCategories(response.data);
      } else {
        showError('Failed to fetch categories', response.error || 'Unable to fetch categories.');
      }
    } catch (err) {
      showError('Error', 'An error occurred while fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    // If category doesn't have _id, reload from API first
    if (!category._id) {
      try {
        const reloadedCategories = await apiService.getCategories();
        if (reloadedCategories.status && reloadedCategories.data) {
          setCategories(reloadedCategories.data);
          // Try to find the category by name
          const categoryWithId = reloadedCategories.data.find(
            c => c.name === category.name
          );
          if (categoryWithId && categoryWithId._id) {
            // Now delete with the correct _id
            try {
              const response = await apiService.deleteCategory(categoryWithId._id);
              if (response.status) {
                // Clear localStorage to force fresh fetch on next load
                localStorage.removeItem('categories');
                setCategories(reloadedCategories.data.filter(c => c._id !== categoryWithId._id));
                showSuccess('Category deleted successfully.');
              } else {
                showError('Failed to delete category', response.error || 'Unable to delete category.');
              }
            } catch (err) {
              showError('Error', 'An error occurred while deleting the category.');
            }
          } else {
            showError('Error', 'Category not found. It may have already been deleted.');
          }
        } else {
          showError('Error', 'Failed to reload categories. Please try again.');
        }
      } catch (err) {
        showError('Error', 'An error occurred while reloading categories.');
      }
      return;
    }

    try {
      const response = await apiService.deleteCategory(category._id);
      if (response.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('categories');
        setCategories(categories.filter(c => c._id !== category._id));
        showSuccess('Category deleted successfully.');
      } else {
        showError('Failed to delete category', response.message || 'Unable to delete category.');
      }
    } catch (err) {
      showError('Error', 'An error occurred while deleting the category.');
    }
  };

  const handleFormClose = () => {
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
    setIsFormOpen(false);
    setEditingCategory(undefined);
    setExtraFields([]);
    reset({
      name: ''
    });
  };

  const handleEdit = async (category: Category) => {
    // If category doesn't have _id, reload from API first
    if (!category._id) {
      try {
        const reloadedCategories = await apiService.getCategories();
        if (reloadedCategories.status && reloadedCategories.data) {
          setCategories(reloadedCategories.data);
          const categoryWithId = reloadedCategories.data.find(
            c => c.name === category.name
          );
          if (categoryWithId) {
            setEditingCategory(categoryWithId);
            setExtraFields(categoryWithId.extraField || []);
            reset({
              ...categoryWithId,
              name: formatCategoryName(categoryWithId.name)
            });
            window.history.pushState({ modalOpen: true }, '');
            setIsFormOpen(true);
            return;
          }
        }
        showError('Error', 'Category not found. Please try again.');
      } catch (err) {
        showError('Error', 'An error occurred while reloading categories.');
      }
      return;
    }

    setEditingCategory(category);
    setExtraFields(category.extraField || []);
    reset({
      ...category,
      name: formatCategoryName(category.name)
    });
    window.history.pushState({ modalOpen: true }, '');
    setIsFormOpen(true);
  };

  const onSubmit = async (data: Category) => {
    setIsSubmitting(true);

    try {
      const categoryData = {
        ...data,
        extraField: extraFields.length > 0 ? extraFields : undefined,
      };

      // If editing but category doesn't have _id, reload from API first
      if (editingCategory && !editingCategory._id) {
        await fetchCategories();
        showError('Error', 'Category ID not found. Please try editing again after the page reloads.');
        setIsSubmitting(false);
        return;
      }

      const response = editingCategory
        ? await apiService.updateCategory(editingCategory._id!, {name:categoryData.name})
        : await apiService.createCategory(categoryData);

      if (response.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('categories');
        
        const deriveCategoryFromResponse = (): Category | undefined => {
          const docCandidate = response.doc && !Array.isArray(response.doc) ? response.doc as Category : undefined;
          const dataCandidate = response.data && !Array.isArray(response.data) ? response.data as Category : undefined;
          return docCandidate ?? dataCandidate;
        };

        if (editingCategory) {
          const updatedCategory = deriveCategoryFromResponse() ?? {
            ...editingCategory,
            ...categoryData,
            _id: editingCategory._id,
          };

          setCategories(prev =>
            prev.map(cat => (cat._id === editingCategory._id ? { ...cat, ...updatedCategory } : cat))
          );
        } else {
          const createdCategory = deriveCategoryFromResponse() ?? {
            ...categoryData,
          };
          setCategories(prev => [...prev, createdCategory]);
        }

        showSuccess(editingCategory ? 'Category updated successfully' : 'Category created successfully');
        handleFormClose();
      } else {
        showError('Failed to save category', response.message || 'Unable to save category.');
      }
    } catch (err) {
      showError('Error', 'An error occurred while saving the category.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        {/* <h1 className="text-2xl font-bold text-gray-900">Categories</h1> */}
        <button
          onClick={() => {
            window.history.pushState({ modalOpen: true }, '');
            setIsFormOpen(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category._id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{formatCategoryName(category.name)}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="text-orange-600 hover:text-orange-900"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(category)}
                  className="text-red-600 hover:text-red-900"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            {category.extraField && category.extraField.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {category.extraField.map((field, index) => (
                  <span key={index} className="inline-flex px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                    {field}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {categories.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No categories found. Add your first category to get started.</p>
        </div>
      )}

      {/* Category Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto !mt-0">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-50" 
              onClick={handleFormClose} 
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
                <button
                  onClick={handleFormClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name *
                  </label>
                  <input
                    {...register('name', { required: 'Category name is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter category name"
                    defaultValue={editingCategory ? formatCategoryName(editingCategory.name) : ''}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                  <small className="text-gray-500">Examples: Fashion, Electronics, Shirts, etc.</small>
                </div>
                


                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleFormClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'Saving...' : editingCategory ? 'Update Category' : 'Add Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;